import { z } from 'zod'

export const CORE_SCHEMA_VERSION = 2 as const

export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue }

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() => z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(jsonValueSchema),
  z.record(jsonValueSchema),
]))

export const isoDateTimeSchema = z.string().datetime({ offset: true })

export const coreReferenceKindSchema = z.enum([
  'capture',
  'context',
  'context_relation',
  'plan',
  'action',
  'review',
  'signal',
  'automation_receipt',
  'user',
])

export const coreReferenceSchema = z.object({
  kind: coreReferenceKindSchema,
  id: z.string().min(1),
  role: z.string().min(1).optional(),
})

export const derivationMethodSchema = z.enum(['manual', 'rules', 'model', 'hybrid', 'import'])

export const modelAttemptSchema = z.object({
  processor: z.string().min(1),
  outcome: z.enum(['no_change', 'rejected', 'unavailable']),
  code: z.enum(['timeout', 'response_contract', 'service', 'proposal_validation', 'unknown']).optional(),
})

export const derivationSchema = z.object({
  method: derivationMethodSchema,
  processor: z.string().min(1),
  version: z.string().min(1),
  confidence: z.number().min(0).max(1),
  sourceRefs: z.array(coreReferenceSchema).min(1),
  modelAttempt: modelAttemptSchema.optional(),
})

const baseRecordShape = {
  schemaVersion: z.literal(CORE_SCHEMA_VERSION),
  id: z.string().min(1),
  userId: z.string().min(1),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
}

export const textCaptureInputSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('text'),
  content: z.string().min(1),
  entryMethod: z.enum(['keyboard', 'dictation', 'paste', 'external']).default('keyboard'),
})

export const voiceCaptureInputSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('voice'),
  storagePath: z.string().min(1),
  durationMs: z.number().int().nonnegative().optional(),
  transcript: z.string().min(1).optional(),
})

export const imageCaptureInputSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('image'),
  storagePath: z.string().min(1),
  mimeType: z.string().min(1),
  altText: z.string().min(1).optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  byteSize: z.number().int().positive().optional(),
  hash: z.string().min(8).optional(),
})

export const linkCaptureInputSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('link'),
  url: z.string().url(),
  title: z.string().min(1).optional(),
})

export const fileCaptureInputSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('file'),
  storagePath: z.string().min(1),
  mimeType: z.string().min(1),
  name: z.string().min(1),
})

export const locationCaptureInputSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('location'),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  label: z.string().min(1).optional(),
})

export const captureInputSchema = z.discriminatedUnion('kind', [
  textCaptureInputSchema,
  voiceCaptureInputSchema,
  imageCaptureInputSchema,
  linkCaptureInputSchema,
  fileCaptureInputSchema,
  locationCaptureInputSchema,
])

export const captureSchema = z.object({
  ...baseRecordShape,
  status: z.enum(['saved', 'processing', 'processed', 'needs_review', 'failed', 'archived']),
  inputs: z.array(captureInputSchema).min(1),
  primaryText: z.string().optional(),
  processingVersion: z.string().min(1).optional(),
  idempotencyKey: z.string().min(1),
  outputRefs: z.array(coreReferenceSchema).default([]),
  processedAt: isoDateTimeSchema.optional(),
  errorCode: z.string().min(1).optional(),
})

export const contextKindSchema = z.enum([
  'episode',
  'person',
  'place',
  'organization',
  'project',
  'topic',
  'preference',
  'personal_state',
  'motivation',
  'constraint',
  'decision',
  'routine',
  'obligation',
  'reference',
])

export const contextFormSchema = z.enum([
  'profile',
  'observation',
  'thread',
  'collection',
  'reference',
])

export const contextFactSchema = z.object({
  key: z.string().min(1),
  value: z.union([z.string(), z.number().finite(), z.boolean(), z.null()]),
  confidence: z.number().min(0).max(1).optional(),
})

export const contextObservationSchema = z.object({
  observedAt: isoDateTimeSchema,
  startedAt: isoDateTimeSchema.optional(),
  endedAt: isoDateTimeSchema.optional(),
  value: z.union([z.string(), z.number().finite(), z.boolean(), z.null()]).optional(),
  unit: z.string().min(1).optional(),
})

export const contextRecordSchema = z.object({
  ...baseRecordShape,
  form: contextFormSchema,
  kind: contextKindSchema,
  status: z.enum(['active', 'archived']),
  confirmation: z.enum(['inferred', 'pending', 'confirmed', 'corrected', 'rejected']),
  title: z.string().min(1),
  summary: z.string().optional(),
  tydyCommentary: z.string().optional(),
  body: z.string().optional(),
  facts: z.array(contextFactSchema).default([]),
  observation: contextObservationSchema.optional(),
  memberRefs: z.array(coreReferenceSchema).default([]),
  sourceRefs: z.array(coreReferenceSchema).min(1),
  relatedRefs: z.array(coreReferenceSchema).default([]),
  derivation: derivationSchema,
}).superRefine((record, context) => {
  if (record.form === 'observation' && !record.observation) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['observation'],
      message: 'Observation context requires observation timing.',
    })
  }

  if (record.observation?.startedAt && record.observation.endedAt) {
    if (new Date(record.observation.endedAt).getTime() < new Date(record.observation.startedAt).getTime()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['observation', 'endedAt'],
        message: 'Observation end must not be before its start.',
      })
    }
  }
})

export const contextRelationTypeSchema = z.enum([
  'about',
  'involves',
  'supports',
  'affects',
  'part_of',
  'related_to',
  'preference_for',
  'correlates_with',
])

const contextRecordReferenceSchema = coreReferenceSchema.extend({
  kind: z.literal('context'),
})

export const contextRelationSchema = z.object({
  ...baseRecordShape,
  status: z.enum(['active', 'rejected']),
  confirmation: z.enum(['inferred', 'pending', 'confirmed', 'corrected', 'rejected']),
  relation: contextRelationTypeSchema,
  fromRef: contextRecordReferenceSchema,
  toRef: contextRecordReferenceSchema,
  evidenceRefs: z.array(coreReferenceSchema).min(1),
  derivation: derivationSchema,
})

export const planSchema = z.object({
  ...baseRecordShape,
  status: z.enum(['draft', 'active', 'paused', 'completed', 'archived']),
  title: z.string().min(1),
  purpose: z.string().optional(),
  contextRefs: z.array(coreReferenceSchema).default([]),
  sourceRefs: z.array(coreReferenceSchema).default([]),
  targetStartAt: isoDateTimeSchema.optional(),
  targetEndAt: isoDateTimeSchema.optional(),
  completedAt: isoDateTimeSchema.optional(),
  idempotencyKey: z.string().min(1).optional(),
})

export const actionSchema = z.object({
  ...baseRecordShape,
  planId: z.string().min(1).optional(),
  status: z.enum(['proposed', 'ready', 'in_progress', 'blocked', 'completed', 'dismissed']),
  title: z.string().min(1),
  details: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high']).default('medium'),
  order: z.number().int().nonnegative().default(0),
  dueAt: isoDateTimeSchema.optional(),
  scheduledFor: isoDateTimeSchema.optional(),
  completedAt: isoDateTimeSchema.optional(),
  contextRefs: z.array(coreReferenceSchema).default([]),
  sourceRefs: z.array(coreReferenceSchema).default([]),
  idempotencyKey: z.string().min(1).optional(),
})

export const reviewChoiceSchema = z.object({
  id: z.string().min(1),
  labelKey: z.string().min(1),
  operation: z.enum(['confirm', 'correct', 'merge', 'apply', 'dismiss', 'retry']),
})

export const reviewItemSchema = z.object({
  ...baseRecordShape,
  type: z.enum(['interpretation', 'identity', 'consequence', 'correction', 'processing_failure']),
  status: z.enum(['pending', 'resolved', 'dismissed']),
  title: z.string().min(1),
  promptKey: z.string().min(1),
  subjectRefs: z.array(coreReferenceSchema).min(1),
  evidenceRefs: z.array(coreReferenceSchema).default([]),
  recommendedChoiceId: z.string().min(1).optional(),
  choices: z.array(reviewChoiceSchema).min(1),
  receiptId: z.string().min(1).optional(),
  resolvedChoiceId: z.string().min(1).optional(),
  resolvedAt: isoDateTimeSchema.optional(),
})

export const signalScoreSchema = z.object({
  confidence: z.number().min(0).max(1),
  usefulness: z.number().min(0).max(1),
  urgency: z.number().min(0).max(1),
  annoyanceRisk: z.number().min(0).max(1),
  total: z.number().min(0).max(1),
})

export const signalSchema = z.object({
  ...baseRecordShape,
  kind: z.enum(['reminder', 'pattern', 'change', 'risk', 'opportunity', 'summary']),
  status: z.enum(['active', 'seen', 'acted', 'dismissed', 'expired']),
  title: z.string().min(1),
  summary: z.string().min(1),
  evidenceRefs: z.array(coreReferenceSchema).min(1),
  relatedRefs: z.array(coreReferenceSchema).default([]),
  score: signalScoreSchema,
  derivation: derivationSchema,
  surfacedAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema.optional(),
  seenAt: isoDateTimeSchema.optional(),
  resolvedAt: isoDateTimeSchema.optional(),
  fingerprint: z.string().min(1),
})

export const automationChangeSchema = z.object({
  operation: z.enum(['create', 'update', 'archive', 'link', 'unlink']),
  targetRef: coreReferenceSchema,
  before: jsonValueSchema.optional(),
  after: jsonValueSchema.optional(),
})

export const automationReceiptSchema = z.object({
  ...baseRecordShape,
  status: z.enum(['applied', 'needs_review', 'reverted', 'failed']),
  operation: z.string().min(1),
  summary: z.string().min(1),
  reason: z.string().min(1),
  inputRefs: z.array(coreReferenceSchema).min(1),
  outputRefs: z.array(coreReferenceSchema).default([]),
  changes: z.array(automationChangeSchema).default([]),
  derivation: derivationSchema,
  idempotencyKey: z.string().min(1),
  reversible: z.boolean(),
  reviewItemId: z.string().min(1).optional(),
  revertedAt: isoDateTimeSchema.optional(),
})

export type CoreReference = z.infer<typeof coreReferenceSchema>
export type Derivation = z.infer<typeof derivationSchema>
export type CaptureInput = z.infer<typeof captureInputSchema>
export type Capture = z.infer<typeof captureSchema>
export type ContextRecord = z.infer<typeof contextRecordSchema>
export type ContextRelation = z.infer<typeof contextRelationSchema>
export type Plan = z.infer<typeof planSchema>
export type Action = z.infer<typeof actionSchema>
export type ReviewItem = z.infer<typeof reviewItemSchema>
export type Signal = z.infer<typeof signalSchema>
export type AutomationReceipt = z.infer<typeof automationReceiptSchema>
